/**
 * CloseCaixaModal — modal de fechamento de caixa.
 * Fonte de verdade: /pos/caixa/resumo + /pos/caixa/fechar (db.caixa).
 * db.cash_registers é sincronizado no backend via _caixa_sync.py.
 */
import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { printCaixaReportThermal } from "@/utils/printService";

export function CloseCaixaModal({ open, onClose, authAxios, onSuccess }) {
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [whatsappNum, setWhatsappNum] = useState("");
  const [email, setEmail] = useState("");
  // { methodKey: "76.30", ... }
  const [contagem, setContagem] = useState({});
  const [expandedMethod, setExpandedMethod] = useState(null);

  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setResumo(null);
    setConfirming(false);
    setContagem({});

    Promise.all([
      authAxiosRef.current.get("/pos/caixa/resumo"),
      authAxiosRef.current.get("/restaurant").catch(() => null),
    ]).then(([resumoRes, restRes]) => {
      setResumo(resumoRes.data);
      const rest = restRes?.data;
      if (rest) {
        setWhatsappNum(prev => prev || rest.whatsapp_number || rest.phone || "");
        setEmail(prev => prev || rest.report_email || rest.email || "");
      }
    }).catch(() => {
      setResumo({ valor_inicial: 0, total_vendas: 0, total_dinheiro: 0, total_cartao: 0, cancelamentos: 0, vendas: [], metodos: {}, pedidos_pendentes: [] });
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fmt = (v) => `€${(v || 0).toFixed(2)}`;
  const fmtDiff = (v) => {
    if (v === null || v === undefined) return null;
    return `${v >= 0 ? "+" : ""}€${Math.abs(v).toFixed(2)}`;
  };

  const metodos = resumo?.metodos || {};
  // esperado_em_caixa = fundo inicial + total em dinheiro (apenas dinheiro físico)
  const esperadoCaixa = (resumo?.valor_inicial || 0) + (resumo?.total_dinheiro || 0);
  const pedidosPendentes = resumo?.pedidos_pendentes || [];

  // Fixed list of standard methods — always shown even with €0
  const DEFAULT_METHODS = ["Dinheiro", "Cartão", "MBWay", "Bizum", "Online"];
  const allMetodos = { ...Object.fromEntries(DEFAULT_METHODS.map(m => [m, 0])), ...metodos };

  const rows = Object.entries(allMetodos).map(([key, registado]) => {
    const fisicoStr = contagem[key] ?? "";
    const fisicoNum = fisicoStr !== "" ? parseFloat(fisicoStr) : null;
    const diff = fisicoNum !== null ? fisicoNum - registado : null;
    return { key, registado, fisicoNum, fisicoStr, diff };
  });

  const totalFisico = rows.every(r => r.fisicoNum !== null)
    ? rows.reduce((s, r) => s + (r.fisicoNum || 0), 0)
    : null;
  const totalRegistado = rows.reduce((s, r) => s + (r.registado || 0), 0);
  const totalDiff = totalFisico !== null ? totalFisico - totalRegistado : null;

  const handleContagem = (key, val) => {
    // Block negative values and NaN — empty string allowed (means "not counted yet")
    if (val !== "" && (isNaN(parseFloat(val)) || parseFloat(val) < 0)) return;
    setContagem(prev => ({ ...prev, [key]: val }));
  };

  const handleFechar = async () => {
    setProcessing(true);
    try {
      const recebido = {};
      rows.forEach(r => {
        if (r.fisicoNum !== null) recebido[r.key] = r.fisicoNum;
      });

      const res = await authAxios.post("/pos/caixa/fechar", {
        enviar_whatsapp: whatsappNum || null,
        enviar_email: email || null,
        recebido_por_metodo: Object.keys(recebido).length > 0 ? recebido : undefined,
        valor_contado: totalFisico !== null ? totalFisico : undefined,
      });
      toast.success("Caixa fechado com sucesso!");
      if (whatsappNum) toast.success(`WhatsApp enviado para ${whatsappNum}`);
      if (res.data?.email_sent) {
        toast.success(`Email enviado para ${res.data.email_address}`);
      } else if (email) {
        const reason = res.data?.email_error || "Erro desconhecido";
        toast.error(`Email não enviado: ${reason}`);
      }
      try {
        const backendResumo = res.data?.resumo || resumo;
        const backendMetodos = backendResumo?.metodos || {};
        const printRows = Object.keys({ ...Object.fromEntries(rows.map(r => [r.key, r])), ...backendMetodos })
          .map(key => {
            const stateRow = rows.find(r => r.key === key);
            return {
              key,
              registado: backendMetodos[key] ?? stateRow?.registado ?? 0,
              recVal: stateRow?.fisicoNum ?? null,
              diff: stateRow?.fisicoNum != null ? stateRow.fisicoNum - (backendMetodos[key] ?? stateRow?.registado ?? 0) : null,
            };
          });
        const backendTotalRegistado = Object.values(backendMetodos).reduce((s, v) => s + (v || 0), 0);
        await printCaixaReportThermal({
          date: new Date().toLocaleString("pt-PT"),
          rows: printRows,
          totalRegistado: backendTotalRegistado,
          totalRecebido: totalFisico,
          totalDiff: totalFisico !== null ? totalFisico - backendTotalRegistado : null,
          valorInicial: backendResumo?.valor_inicial || 0,
          totalVendas: backendResumo?.total_vendas || 0,
          cancelamentos: backendResumo?.cancelamentos || 0,
        });
      } catch (printErr) {
        console.warn("[CaixaModal] Impressão falhou:", printErr?.message);
      }
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Erro ao fechar caixa");
    }
    setProcessing(false);
    setConfirming(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !processing) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-red-600" /> Fechar Caixa
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : confirming ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-amber-800">Confirmar fechamento?</p>
                <p className="text-amber-700">Total sistema: <span className="font-black text-gray-800">{fmt(totalRegistado)}</span></p>
                {totalFisico !== null && (
                  <>
                    <p className="text-amber-700">Total contado: <span className="font-black text-blue-700">{fmt(totalFisico)}</span></p>
                    <p className="font-bold" style={{ color: totalDiff >= 0 ? "#16a34a" : "#dc2626" }}>
                      Diferença: {fmtDiff(totalDiff)}&nbsp;{totalDiff > 0 ? "(sobra)" : totalDiff < 0 ? "(falta)" : "✓ confere"}
                    </p>
                  </>
                )}
                {pedidosPendentes.length > 0 && (
                  <p className="text-red-700 font-semibold mt-1">
                    ⚠ {pedidosPendentes.length} pedido(s) pendente(s) não pagos
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirming(false)} disabled={processing}>Voltar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleFechar} disabled={processing}>
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar fecho"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">

            {/* Aviso de pedidos pendentes — mostrado no topo para ser imediatamente visível */}
            {pedidosPendentes.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-xl border border-amber-300 bg-amber-50">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    {pedidosPendentes.length} pedido(s) pendente(s) não pagos
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Confirma que queres fechar a caixa com pedidos por cobrar?
                  </p>
                  <div className="mt-1 space-y-0.5">
                    {pedidosPendentes.slice(0, 4).map(p => (
                      <p key={p.id} className="text-xs text-amber-700">
                        #{p.numero}{p.mesa ? ` · Mesa ${p.mesa}` : ""} · {fmt(p.total)} · {p.cliente}
                      </p>
                    ))}
                    {pedidosPendentes.length > 4 && (
                      <p className="text-xs text-amber-500">…e mais {pedidosPendentes.length - 4}</p>
                    )}
                  </div>
                  <p className="text-xs text-amber-600 mt-2 font-medium">
                    Procura estes pedidos no separador <strong>OCULTOS</strong> da página de pedidos.
                  </p>
                </div>
              </div>
            )}

            {/* Resumo financeiro */}
            <div className="rounded-xl border p-4 space-y-1 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Fundo inicial</span>
                <span>{fmt(resumo?.valor_inicial)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Vendas em dinheiro</span>
                <span>{fmt(resumo?.total_dinheiro)}</span>
              </div>
              <div className="flex justify-between font-bold text-emerald-600">
                <span>Total vendas (todos os métodos)</span>
                <span>{fmt(resumo?.total_vendas)}</span>
              </div>
              <div className="flex justify-between text-red-500">
                <span>Cancelamentos</span>
                <span>{fmt(resumo?.cancelamentos)}</span>
              </div>
              <div className="border-t pt-2 mt-1 flex justify-between items-start font-black text-base">
                <div>
                  <span>Esperado em caixa</span>
                  <span className="block text-[10px] font-normal text-gray-400">fundo + vendas dinheiro</span>
                </div>
                <span className="text-emerald-600">{fmt(esperadoCaixa)}</span>
              </div>
            </div>

            {/* Contagem por método */}
            <div className="rounded-xl border overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b">
                <h4 className="font-bold text-gray-700 text-sm">Contagem por Método de Pagamento</h4>
                <p className="text-[11px] text-gray-400 mt-0.5">Preenche o que tens fisicamente (valores negativos não são permitidos)</p>
              </div>

              <div className="grid grid-cols-3 gap-2 px-4 py-2 text-[10px] font-bold text-gray-400 uppercase border-b bg-gray-50">
                <span>Método</span>
                <span className="text-right">Sistema</span>
                <span className="text-right">Contado / Diferença</span>
              </div>

              {rows.map(({ key, registado, fisicoStr, diff }) => {
                const methodOrders = resumo?.method_orders?.[key] || [];
                const isExpanded = expandedMethod === key;
                return (
                  <div key={key} className="border-b last:border-0">
                    <div className="grid grid-cols-3 gap-2 px-4 py-3 items-center">
                      <button
                        type="button"
                        onClick={() => methodOrders.length > 0 ? setExpandedMethod(isExpanded ? null : key) : null}
                        className={`flex items-center gap-1 text-left ${methodOrders.length > 0 ? "cursor-pointer hover:text-emerald-700" : "cursor-default"}`}
                      >
                        <span className="text-sm font-semibold text-gray-700 capitalize">{key}</span>
                        {methodOrders.length > 0 && (
                          <span className="text-[10px] text-gray-400 font-normal">({methodOrders.length})</span>
                        )}
                        {methodOrders.length > 0 && (
                          isExpanded ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />
                        )}
                      </button>
                      <span className="text-right text-sm text-gray-500">{fmt(registado)}</span>
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                          value={fisicoStr}
                          onChange={e => handleContagem(key, e.target.value)}
                          className="h-8 text-sm text-right w-24 font-bold"
                        />
                        {diff !== null && (
                          <span className={`text-xs font-bold min-w-[52px] text-right ${diff > 0 ? "text-green-600" : diff < 0 ? "text-red-600" : "text-gray-400"}`}>
                            {diff === 0 ? "✓" : `${diff > 0 ? "+" : ""}€${Math.abs(diff).toFixed(2)}`}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded && methodOrders.length > 0 && (
                      <div className="mx-4 mb-3 rounded-lg bg-gray-50 border border-gray-200 text-xs divide-y divide-gray-100">
                        {methodOrders.map((o) => (
                          <div key={o.order_id} className="flex justify-between items-center px-3 py-1.5">
                            <span className="font-mono font-semibold text-gray-600">#{o.codigo}</span>
                            <span className="text-gray-500 truncate mx-2 max-w-[120px]">{o.cliente || "—"}</span>
                            <span className="text-gray-400">{o.hora}</span>
                            <span className="font-bold text-gray-700 ml-2">{fmt(o.valor)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {rows.length > 0 && (
                <div className="grid grid-cols-3 gap-2 px-4 py-3 bg-gray-50 border-t items-center">
                  <span className="text-sm font-black text-gray-800">Total</span>
                  <span className="text-right text-sm font-black text-gray-800">{fmt(totalRegistado)}</span>
                  <div className="flex items-center gap-1 justify-end">
                    <span className="text-sm font-black text-blue-700 w-24 text-right">
                      {totalFisico !== null ? fmt(totalFisico) : "—"}
                    </span>
                    {totalDiff !== null && (
                      <span className={`text-xs font-black min-w-[52px] text-right ${totalDiff > 0 ? "text-green-600" : totalDiff < 0 ? "text-red-600" : "text-gray-400"}`}>
                        {totalDiff === 0 ? "✓" : `${totalDiff > 0 ? "+" : ""}€${Math.abs(totalDiff).toFixed(2)}`}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Vendas do dia */}
            {resumo?.vendas?.length > 0 && (
              <div className="rounded-xl border p-4">
                <h4 className="font-bold text-gray-700 mb-2 text-sm">Vendas do Dia ({resumo.vendas.length})</h4>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {resumo.vendas.map((v, i) => (
                    <div key={i} className="flex justify-between items-center text-xs border-b pb-1 last:border-0 gap-1">
                      <span className="text-gray-400 shrink-0">{v.hora}</span>
                      {v.codigo && <span className="font-mono font-semibold text-gray-600 shrink-0">#{v.codigo}</span>}
                      <span className="text-gray-400 truncate flex-1 min-w-0">{v.cliente || v.tipo}</span>
                      <span className={`font-semibold shrink-0 ${v.cancelado ? "text-red-400 line-through" : "text-gray-700"}`}>{fmt(v.valor)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Envio relatório */}
            <div className="rounded-xl border p-4 space-y-3">
              <h4 className="font-bold text-gray-700 text-sm">Enviar Relatório</h4>
              <div>
                <label className="text-xs text-gray-500 block mb-1">WhatsApp</label>
                <Input placeholder="+34 600 000 000" value={whatsappNum} onChange={e => setWhatsappNum(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Email</label>
                <Input type="email" placeholder="gerente@restaurante.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {!confirming && !loading && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={processing}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 gap-2" onClick={() => setConfirming(true)}>
              <Lock className="w-4 h-4" /> Fechar Caixa
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
